const PictProvider = require('pict-provider');

/** @typedef {import('./DistillingProvider')} DistillingProvider */
/** @typedef {import('./DistillingProvider').FieldDistillingMapping} FieldDistillingMapping */

/** @typedef {import('../HeadlightConfigurableApplication.js').Pict} Pict */

/**
 * Base class for InputType providers.
 *
 * Ideas:
 *
 * Understand what kind of values the control can accept? Single- vs. multi-value?
 * Understand whether the control is editable / readonly.
 * Understand whether the control can be auto-filled.
 * Understand whether the control is part of a formula? (ex. in case we need to trigger the solver on distilling?)
 * Understand the control's connection to the distilling bundle.
 * Get / set the control value / selection.
 */
class InputTypeProvider
{
	/**
	 * @param {Pict} pict - The Pict instance.
	 * @param {Function} formManifestDefinitionProvider - The form manifest definition.
	 */
	constructor(pict, formManifestDefinitionProvider)
	{
		this.pict = pict;
		this.formManifestDefinitionProvider = formManifestDefinitionProvider;
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {boolean} - Whether the control is supported by this provider.
	 */
	isControlSupported(element)
	{
		return false;
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 *
	 * @return {boolean} - Whether the control is a scalar value (only stores a single selection).
	 */
	isScalar(element, manifestDefinition)
	{
		return true;
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {boolean} - Whether the control is editable.
	 */
	isEditable(element)
	{
		return true;
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {boolean} - Whether the control is aut-fill-able.
	 */
	isAutoFillEnabled(element)
	{
		return true;
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {boolean} - Whether the control is part of a solver formula.
	 */
	isSolved(element)
	{
		return true;
	}

	/**
	 * Get the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 *
	 * @return {string | number | string[]} - the control value.
	 */
	getValue(element, manifestDefinition)
	{
		return '';
	}

	/**
	 * Set the available options for the control (if supported).
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {Array<Array<string>> | Array<string>} options - The options to set. Each array element is [ value, display ]. If there is no display, inner array can be the value string.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	setOptions(element, options, manifestDefinition)
	{
	}

	/**
	 * Set the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {string | number | string[]} value - The value to set.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	setValue(element, value, manifestDefinition)
	{
	}

	/**
	 * Add a control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {string | number | string[]} value - The value to add.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	addValue(element, value, manifestDefinition)
	{
	}

	/**
	 * Remove the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {string | number | string[]} value - The value to remove.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	removeValue(element, value, manifestDefinition)
	{
	}

	/**
	 * Get the input element for the control (where the value is stored in the DOM / has data-i-datum / etc.).
	 *
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {HTMLInputElement} the input element
	 */
	getInputElement(element)
	{
		if (element instanceof HTMLInputElement)
		{
			return element;
		}
		return null;
	}
}

/**
 * Provider for select input types.
 *
 * NOTE: We use select2 for all select inputs.
 */
class SelectInputTypeProvider extends InputTypeProvider
{
	/**
	 * @param {Pict} pict - The Pict instance.
	 * @param {Function} formManifestDefinitionProvider - The form manifest definition.
	 */
	constructor(pict, formManifestDefinitionProvider)
	{
		super(pict, formManifestDefinitionProvider);
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {boolean} - Whether the control is supported by this provider.
	 */
	isControlSupported(element)
	{
		if (element.tagName == 'SELECT')
		{
			return true;
		}
		// if the control is the hidden control, resolve over to the select control
		const selectElement = document.getElementById('SELECT-FOR-' + element.id);
		if (selectElement && selectElement.tagName == 'SELECT')
		{
			return true;
		}
		return false;
	}

	/**
	 * Get the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 *
	 * @return {string | number | string[]} - the control value.
	 */
	getValue(element, manifestDefinition)
	{
		return $(element).val();
	}

	/**
	 * Set the available options for the control (if supported).
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {Array<Array<string>> | Array<string>} options - The options to set. Each array element is [ value, display ]. If there is no display, inner array can be the value string.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	setOptions(element, options, manifestDefinition)
	{
		/** @type {Array<{ id: string, text: string }>} */
		const select2Options = options.map((option) =>
		{
			if (Array.isArray(option))
			{
				return { id: option[0], text: option[1] || option[0] || '(no name)' };
			}
			return { id: option, text: option };
		});
		const inputElement = this.getInputElement(element);
		if (!inputElement)
		{
			this.pict.log.error(`SelectInputTypeProvider::setOptions: Could not find input element for select control.`, { element });
			return;
		}
		const datumKey = inputElement.getAttribute('data-i-datum');
		this.setSelect2OptionsForDatumKey(datumKey, select2Options);
	}

	/**
	 * Cache available values for a given field.
	 *
	 * @param {String} datumKey - The datum key to cache values for.
	 * @param {Array<{ id: string, text: string }>} namesAndValues - The values to cache.
	 */
	setSelect2OptionsForDatumKey(datumKey, namesAndValues)
	{
		const cache = this.pict.AppData.Select2OptionsCache = this.pict.AppData.Select2OptionsCache || { };
		cache[datumKey] = namesAndValues;
		this.pict.AppData.Select2OptionsCache = cache;
	}

	/**
	 * Set the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {string | number | string[]} value - The value to set.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	setValue(element, value, manifestDefinition)
	{
		$(element).val(value).trigger('change');
	}

	/**
	 * Set the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {string | number} value - The value to add.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	addValue(element, value, manifestDefinition)
	{
		if (this.isScalar(element, manifestDefinition))
		{
			this.setValue(element, value, manifestDefinition);
			return;
		}

		let selection = $(element).val();
		if (!Array.isArray(selection))
		{
			selection = [ String(selection) ];
		}
		if (selection.includes(String(value)))
		{
			return;
		}
		$(element).val(selection.concat([ String(value) ])).trigger('change');
	}

	/**
	 * Remove a value from the control.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {string | number | string[]} value - The value to remove.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 */
	removeValue(element, value, manifestDefinition)
	{
		if (this.isScalar(element, manifestDefinition))
		{
			this.setValue(element, '', manifestDefinition);
			return;
		}

		let selection = $(element).val();
		if (!Array.isArray(selection))
		{
			selection = [ String(selection) ];
		}
		if (!selection.includes(String(value)))
		{
			return;
		}
		$(element).val(selection.filter((i) => i == value)).trigger('change');
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 * @param {Object} manifestDefinition - The manifest definition for the element.
	 *
	 * @return {boolean} - Whether the control is a scalar value (only stores a single selection).
	 */
	isScalar(element, manifestDefinition)
	{
		//FIXME: made up this property - does it exist?
		return !manifestDefinition?.MultiSelect;
	}

	/**
	 * Get the input element for the control (where the value is stored in the DOM / has data-i-datum / etc.).
	 *
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {HTMLInputElement} the input element
	 */
	getInputElement(element)
	{
		if (element instanceof HTMLSelectElement && element.id.startsWith('SELECT-FOR-'))
		{
			const hiddenElementID = element.id.replace('SELECT-FOR-', '');
			const hiddenInput = document.getElementById(hiddenElementID);
			if (!hiddenInput || !(hiddenInput instanceof HTMLInputElement))
			{
				this.pict.fable.log.error(`Could not find datum element for select; referenced element not found: ${hiddenElementID}`, { element });
				return null;
			}
			return hiddenInput;
		}
		if (element instanceof HTMLInputElement)
		{
			const selectElementID = 'SELECT-FOR-' + element.id;
			const selectInput = document.getElementById(selectElementID);
			if (!selectInput || !(selectInput instanceof HTMLSelectElement))
			{
				this.pict.fable.log.error(`Could not find datum element for select; referenced element not found: ${element.id}`, { element });
				return null;
			}
			return element;
		}
		this.pict.fable.log.error(`Could not find datum element for select; malformed ID: ${element.id}`, { element });
		return null;
	}
}

/**
 * Provider for text input types.
 */
class TextInputTypeProvider extends InputTypeProvider
{
	/**
	 * @param {Pict} pict - The Pict instance.
	 * @param {Function} formManifestDefinitionProvider - The form manifest definition.
	 */
	constructor(pict, formManifestDefinitionProvider)
	{
		super(pict, formManifestDefinitionProvider);
	}

	/**
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {boolean} - Whether the control is supported by this provider.
	 */
	isControlSupported(element)
	{
		return element instanceof HTMLInputElement;
	}

	/**
	 * Get the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {string | number | string[]} - the control value.
	 */
	getValue(element)
	{
		if (element instanceof HTMLInputElement)
		{
			return element.value;
		}
		return element.innerText;
	}

	/**
	 * Set the control value.
	 *
	 * @param {HTMLElement} element - The control element.
	 * @param {string | number | string[]} value - The value to set.
	 */
	setValue(element, value)
	{
		if (Array.isArray(value))
		{
			value = value.join(', ');
		}
		if (typeof value == 'number')
		{
			value = String(value);
		}
		if (element instanceof HTMLInputElement)
		{
			element.value = value;
		}
		element.innerText = value;
	}
}

/**
 * Provider to add behaviors to interface with views / controls based on InputType.
 */
class InputTypeBehaviorProvider extends PictProvider
{
	/**
	 * @param {any} pFable - Fable instance
	 * @param {Object} pOptions - Options for the provider
	 * @param {String} pServiceHash - Hash of the service
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/** @type {Array<InputTypeProvider>} */
		this.providers = [];

		this.addTypeProvider(SelectInputTypeProvider);
		this.setFallbackProvider(this.addTypeProvider(TextInputTypeProvider));
	}

	/**
	 * @return {DistillingProvider}
	 */
	get distilling()
	{
		return this.pict.providers.Distilling;
	}

	/**
	 * Validate a manifest descriptor.
	 *
	 * @param {Object} descriptor - The descriptor to validate.
	 */
	static validateDescriptor(descriptor)
	{
		//FIXME: how to do branching at this point?
		if (descriptor.DataType?.toLowerCase() == 'select' && !Array.isArray(descriptor?.PictForm?.SelectOptions))
		{
			if (descriptor.PictForm?.DistillingMapping)
			{
				console.info(`Descriptor for ${descriptor.Hash} is a select control with distilling mapping. Values will be populated just in time.`,
					{ mapping: descriptor.PictForm.DistillingMapping });
			}
			else
			{
				// TODO: We need to accurately account for "server-side paged" Select2 flow here. This warning can be a red herring.
				console.warn(`Descriptor for ${descriptor.Hash} is a select control but has no options provided.`, { descriptor });
				descriptor.SelectOptions = [ 'no options provided' ]; //FIXME: seems dumb - select2 currently renders weird if there is nothing in here
			}
		}
	}

	/** @typedef {typeof InputTypeProvider} InputTypeProviderClass */

	/**
	 * Add an InputType specific provider.
	 *
	 * @param {InputTypeProviderClass} TypeProviderClass - The provider to add.
	 *
	 * @return {InputTypeProvider} - The provider instance
	 */
	addTypeProvider(TypeProviderClass)
	{
		const provider = new TypeProviderClass(this.pict, () => this.formManifestDefinition);
		this.providers.push(provider);
		return provider;
	}

	/**
	 * Set the fallback provider. Used if no registered provider claims the control.
	 *
	 * @param {InputTypeProvider} provider - The provider to use
	 */
	setFallbackProvider(provider)
	{
		this.fallbackProvider = provider;
	}

	/**
	 * Get the provider for an element.
	 *
	 * @param {HTMLElement} element - The element to get the provider for.
	 *
	 * @return {InputTypeProvider} - The provider for the element.
	 */
	getProviderForElement(element)
	{
		for (const provider of this.providers)
		{
			if (provider.isControlSupported(element))
			{
				return provider;
			}
		}
		return this.fallbackProvider;
	}

	/**
	 * Handle Initialize lifecycle event.
	 */
	onInitialize()
	{
		if (!this.pict.AppData.Interactive)
		{
			return true;
		}

		this.registerSelect2Plugins();
		return super.onInitialize();
	}

	/**
	 * Register custom select2 plugins. Currently, a local pagination adapter.
	 */
	registerSelect2Plugins()
	{
		// idea: use this to get distilling options, or uhh, whatever we got
		const getOptions = (adapter) =>
		{
			return this.pict.AppData.Select2OptionsCache?.[adapter.options.options.datumKey] || [];
		};

		//NOTE: the select2 types don't include define here, but this works
		// @ts-ignore
		$.fn.select2.amd.define('select2/data/localPaginationAdapter', [
			'select2/data/array',
			'select2/utils',
		],
		function(ArrayAdapter, Utils)
		{
			/**
			 * The local pagination adapter "class".
			 *
			 * @param {JQuery<HTMLElement>} $element - The element to attach the adapter to.
			 * @param {any} options - The options for the adapter.
			 */
			function LocalPaginationAdapter($element, options)
			{
				//NOTE: the __super__ isn't recognized because the extension APIs / types aren't in the generated types
				// @ts-ignore
				LocalPaginationAdapter.__super__.constructor.call(this, $element, options);
			}
			Utils.Extend(LocalPaginationAdapter, ArrayAdapter); // eslint-disable-line new-cap

			LocalPaginationAdapter.prototype.query = function(params, callback)
			{
				const fullData = getOptions(this);
				//TODO: customizable
				const pageSize = 100;

				if (!('page' in params))
				{
					params.page = 1;
				}
				const term = (params.term || '').toLowerCase();
				let results;
				if (term == '')
				{
					results = fullData;
				}
				else
				{
					results = $.map(fullData, function(obj)
					{
						//TODO: something more locale friendly
						if (obj.text && obj.text.toLowerCase().includes(term))
						{
							return obj;
						}
					});
				}
				callback({
					results: results.slice((params.page - 1) * pageSize, params.page * pageSize),
					pagination: {
						more: results.length >= params.page * pageSize,
					},
				});
			};

			return LocalPaginationAdapter;
		});
	}

	/**
	 * Handle document loaded event.
	 *
	 * @param {Object<String, any>} data - The loaded data.
	 */
	async onDataLoad(data)
	{
		//FIXME: once lifecycle exists for real: await super.onDataLoad(data);
		this.formManifestDefinition = this.pict.settings.DefaultFormManifest;
	}

	/**
	 * Get the manifest definition for an element.
	 *
	 * @param {HTMLElement} element - The element to get the manifest definition for.
	 *
	 * @return {Object} - The manifest definition for the element.
	 */
	getElementManifestDefinition(element)
	{
		//TODO: also look at data-i-form..? do we need to since it's a manifest key and can't be duplicated? not sure
		const inputElement = this.getInputElementForControlElement(element);
		if (!inputElement)
		{
			this.pict.log.error(`SelectInputTypeProvider::setOptions: Could not find input element for select control.`, { element });
			return;
		}
		const elementDatumKey = inputElement.getAttribute('data-i-datum');
		const manifestDefinition = this.formManifestDefinition;
		return manifestDefinition?.Descriptors?.[elementDatumKey];
	}

	/**
	 * Set the available options for the control (if supported).
	 *
	 * @param {String} selector - A CSS selector for the (single) control.
	 * @param {Array<Array<string>> | Array<string>} options - The options to set. Each array element is [ value, display ]. If there is no display, inner array can be the value string.
	 */
	setOptions(selector, options)
	{
		const matchingControls = $(selector);
		if (matchingControls.length > 1)
		{
			this.pict.log.warn(`InputType::setOptions: More than one element matched selector ${selector}.`);
		}
		const element = matchingControls[0];
		if (!element)
		{
			this.pict.log.warn(`InputType::setOptions: Could not find element matching: ${selector}`);
			return;
		}
		const provider = this.getProviderForElement(element);
		if (!provider)
		{
			this.pict.log.warn(`Could not find provider for element ${element}`);
			return;
		}
		const manifestDefinition = this.getElementManifestDefinition(element);
		provider.setOptions(element, options, manifestDefinition);
	}

	/**
	 * Get the input (value storage) element for a control.
	 *
	 * @param {String} selector - A CSS selector for the (single) control.
	 *
	 * @return {HTMLInputElement} - The input element.
	 */
	getInputElement(selector)
	{
		const matchingControls = $(selector);
		if (matchingControls.length > 1)
		{
			this.pict.log.warn(`InputType::setOptions: More than one element matched selector ${selector}.`);
		}
		const element = matchingControls[0];
		if (!element)
		{
			this.pict.log.warn(`InputType::setOptions: Could not find element matching: ${selector}`);
			return;
		}
		return this.getInputElementForControlElement(element);
	}

	/**
	 * Get the input (value storage) element for a control based on one element.
	 *
	 * @param {HTMLElement} element - The control element.
	 *
	 * @return {HTMLInputElement} - The input element.
	 */
	getInputElementForControlElement(element)
	{
		const provider = this.getProviderForElement(element);
		if (!provider)
		{
			this.pict.log.warn(`Could not find provider for element ${element}`);
			return;
		}
		return provider.getInputElement(element);
	}

	/**
	 * Set a control's value.
	 *
	 * @param {String} selector - A CSS selector for the (single) control.
	 * @param {String} value - The value to set.
	 */
	setValue(selector, value)
	{
		const matchingControls = $(selector);
		if (matchingControls.length > 1)
		{
			this.pict.log.warn(`InputType::setValue: More than one element matched selector ${selector}.`);
		}
		const element = matchingControls[0];
		if (!element)
		{
			this.pict.log.warn(`InputType::setValue: Could not find element matching: ${selector}`);
			return;
		}
		const provider = this.getProviderForElement(element);
		if (!provider)
		{
			this.pict.log.warn(`Could not find provider for element ${element}`);
			return;
		}
		const manifestDefinition = this.getElementManifestDefinition(element);
		provider.setValue(element, value, manifestDefinition);
	}

	/**
	 * Get a control's value.
	 *
	 * @param {String} selector - A CSS selector for the (single) control.
	 *
	 * @return {string | number | string[]} - the control value.
	 */
	getValue(selector)
	{
		const matchingControls = $(selector);
		if (matchingControls.length > 1)
		{
		this.pict.log.warn(`InputType::getValue: More than one element matched selector ${selector}.`);
		}
		const element = matchingControls[0];
		if (!element)
		{
			this.pict.log.warn(`Could not find element matching: ${selector}`);
			return '';
		}
		const provider = this.getProviderForElement(element);
		if (!provider)
		{
			this.pict.log.warn(`Could not find provider for element ${element}`);
			return '';
		}
		const manifestDefinition = this.getElementManifestDefinition(element);
		return provider.getValue(element, manifestDefinition);
	}

	/**
	 * Initialize any uninitialized controls on the DOM.
	 *
	 * TODO: figure out how to distrubute the logic into control specific providers in a sane way
	 */
	initializeControls()
	{
		if (this.pict.AppData.DistillingMapping?.length > 0)
		{
			if (this.distilling)
			{
				//FIXME: how does this work with multiple mappings?
				this.distilling.registerDistilling(this.pict.AppData.DistillingMapping);
			}
		}
	}
}

module.exports = InputTypeBehaviorProvider;
